import * as React from "react";
import { type VariantProps } from "class-variance-authority";
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "../../lib/utils";
import { alertVariants, badgeVariants, chipVariants } from "./variants";

/* ------------------------------------------------------------------- Card */

export type CardProps = React.HTMLAttributes<HTMLDivElement> & {
  /** Lifts on hover; use for clickable cards */
  interactive?: boolean;
};

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, interactive, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "rounded-xl border border-gray-200 bg-white p-5",
        interactive && "cursor-pointer transition-colors hover:border-gray-300",
        className
      )}
      {...props}
    />
  )
);
Card.displayName = "Card";

export const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex flex-col gap-1", className)} {...props} />
  )
);
CardHeader.displayName = "CardHeader";

export const CardTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn("text-sm font-semibold text-gray-900", className)}
      {...props}
    />
  )
);
CardTitle.displayName = "CardTitle";

export const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn("text-sm text-gray-500", className)} {...props} />
));
CardDescription.displayName = "CardDescription";

export const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn(className)} {...props} />
);
CardContent.displayName = "CardContent";

export const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex items-center", className)} {...props} />
  )
);
CardFooter.displayName = "CardFooter";

/* ------------------------------------------------------------------ Panel */

export interface PanelProps extends Omit<React.HTMLAttributes<HTMLElement>, "title"> {
  title?: React.ReactNode;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  /** Small count pill after the title */
  count?: React.ReactNode;
  /** Remove body padding (tables, lists that run edge to edge) */
  flush?: boolean;
}

/** Card with a titled header row — the standard dashboard section */
export const Panel: React.FC<PanelProps> = ({
  title,
  description,
  actions,
  count,
  flush,
  className,
  children,
  ...props
}) => (
  <section
    className={cn("overflow-hidden rounded-xl border border-gray-200 bg-white", className)}
    {...props}
  >
    {(title || actions) && (
      <header className="flex min-h-12 items-center justify-between gap-4 border-b border-gray-100 px-5 py-3">
        <div className="min-w-0">
          {title && (
            <h2 className="flex items-center gap-2 text-title font-semibold text-gray-900">
              {title}
              {count !== undefined && count !== null && (
                <span className="tabular inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-gray-100 px-1.5 text-xs font-medium text-gray-600">
                  {count}
                </span>
              )}
            </h2>
          )}
          {description && <p className="mt-0.5 text-meta text-gray-500">{description}</p>}
        </div>
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </header>
    )}
    <div className={cn(!flush && "p-5")}>{children}</div>
  </section>
);

/* ------------------------------------------------------------------ Badge */

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  /** Leading status dot */
  dot?: boolean;
  /** Override the dot colour, e.g. "bg-amber-500" on a glass badge */
  dotClassName?: string;
}

const dotColor: Record<string, string> = {
  neutral: "bg-gray-400",
  brand: "bg-brand-500",
  success: "bg-emerald-500",
  warning: "bg-amber-500",
  danger: "bg-red-500",
  info: "bg-sky-500",
  violet: "bg-violet-500",
  dark: "bg-white",
  glass: "bg-emerald-500",
  "brand-solid": "bg-white",
};

export const Badge: React.FC<BadgeProps> = ({ tone, size, dot, dotClassName, className, children, ...props }) => (
  <span className={cn(badgeVariants({ tone, size }), className)} {...props}>
    {dot && <span aria-hidden className={cn("size-1.5 rounded-full", dotColor[tone ?? "neutral"], dotClassName)} />}
    {children}
  </span>
);

/* ------------------------------------------------------------------- Chip */

export interface ChipProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof chipVariants> {
  selected?: boolean;
}

export const Chip = React.forwardRef<HTMLButtonElement, ChipProps>(
  ({ className, variant, size, selected, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      aria-pressed={selected}
      className={cn(chipVariants({ variant: selected ? "primary" : variant ?? "default", size }), className)}
      {...props}
    />
  )
);
Chip.displayName = "Chip";

/* ------------------------------------------------------------------ Alert */

const alertIcons = {
  info: <Info className="size-5 text-sky-600" />,
  success: <CheckCircle2 className="size-5 text-emerald-600" />,
  warning: <AlertTriangle className="size-5 text-amber-600" />,
  error: <XCircle className="size-5 text-red-600" />,
};

export interface AlertProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title">,
    VariantProps<typeof alertVariants> {
  variant?: "info" | "success" | "warning" | "error";
  title?: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode;
  showDefaultIcon?: boolean;
  dismissible?: boolean;
  onDismiss?: () => void;
  /** Optional action area (buttons/links) under the text */
  action?: React.ReactNode;
}

export const Alert: React.FC<AlertProps> = ({
  variant = "info",
  title,
  description,
  icon,
  showDefaultIcon = true,
  dismissible,
  onDismiss,
  action,
  className,
  children,
  ...props
}) => {
  const displayIcon = icon ?? (showDefaultIcon ? alertIcons[variant] : null);
  return (
    <div role="alert" className={cn(alertVariants({ variant }), className)} {...props}>
      {displayIcon && <div className="shrink-0 pt-px">{displayIcon}</div>}
      <div className="min-w-0 flex-1">
        {title && <p className="font-semibold">{title}</p>}
        {description && <p className={cn("opacity-90", title && "mt-1")}>{description}</p>}
        {children && <div className={cn("opacity-90", (title || description) && "mt-1")}>{children}</div>}
        {action && <div className="mt-3 flex flex-wrap gap-2">{action}</div>}
      </div>
      {dismissible && onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="-m-1 h-fit shrink-0 rounded-md p-1 opacity-60 transition-opacity hover:opacity-100"
        >
          <X className="size-4" />
        </button>
      )}
    </div>
  );
};

/* --------------------------------------------------------------- Skeleton */

export const Skeleton: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
  <div className={cn("animate-shimmer rounded-md bg-gray-100", className)} {...props} />
);

/* ---------------------------------------------------------------- Spinner */

export const Spinner: React.FC<{ className?: string; label?: string }> = ({ className, label }) => (
  <div role="status" className="flex items-center justify-center gap-3 text-sm text-gray-500">
    <span
      aria-hidden
      className={cn(
        "size-5 animate-spin rounded-full border-2 border-gray-200 border-t-brand-600",
        className
      )}
    />
    {label ? <span>{label}</span> : <span className="sr-only">Loading</span>}
  </div>
);

/* ------------------------------------------------------------- EmptyState */

export interface EmptyStateProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  icon?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
  /** Render without the dashed container */
  bare?: boolean;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  action,
  className,
  bare,
  ...props
}) => (
  <div
    {...props}
    className={cn(
      "flex flex-col items-center justify-center px-6 text-center",
      bare ? "py-8" : "rounded-xl border border-dashed border-gray-300 py-12",
      className
    )}
  >
    {icon && !bare && <div className="mb-3 text-gray-400 [&_svg]:size-6 [&_svg]:stroke-[1.5]">{icon}</div>}
    <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
    {description && <p className="mt-1 max-w-sm text-meta leading-5 text-gray-500">{description}</p>}
    {action && <div className="mt-4 flex flex-wrap justify-center gap-2">{action}</div>}
  </div>
);

/* --------------------------------------------------------------- StatCard */

export interface StatCardProps {
  label: React.ReactNode;
  value: React.ReactNode;
  /** @deprecated Stat tiles no longer render icons; kept so call sites compile */
  icon?: React.ReactNode;
  /** Small supporting line, e.g. "+3 this week" */
  hint?: React.ReactNode;
  /** @deprecated Ignored: tiles are neutral */
  tone?: "neutral" | "brand" | "success" | "warning" | "info" | "violet";
  onClick?: () => void;
  /** Render the tile as a router link */
  to?: string;
  /** Show a skeleton in place of the value */
  loading?: boolean;
  /** Marks the tile as the current filter (adds a brand ring) */
  active?: boolean;
  /** "sm" uses a smaller value for text such as dates or tier names */
  size?: "md" | "sm";
  className?: string;
}

export const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  hint,
  onClick,
  to,
  loading,
  active,
  size = "md",
  className,
}) => {
  const interactive = Boolean(onClick || to);
  const classes = cn(
    "group flex w-full flex-col rounded-xl border border-gray-200 bg-white px-4 py-3.5 text-left",
    interactive && "transition-colors hover:border-gray-300 hover:bg-gray-50/60",
    active && "border-gray-900 ring-1 ring-gray-900",
    className
  );
  const body = (
    <>
      <span className="text-meta text-gray-500">{label}</span>
      <span
        className={cn(
          "tabular mt-1.5 block font-semibold tracking-tight text-gray-900",
          size === "sm" ? "text-base leading-6" : "text-2xl leading-8"
        )}
      >
        {loading ? (
          <span aria-hidden className="my-1 block h-6 w-14 animate-shimmer rounded-md bg-gray-100" />
        ) : (
          value
        )}
      </span>
      {hint && <span className="mt-0.5 text-xs text-gray-500">{hint}</span>}
    </>
  );
  if (to) {
    return (
      <Link to={to} onClick={onClick} className={classes}>
        {body}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button type="button" onClick={onClick} aria-pressed={active} className={classes}>
        {body}
      </button>
    );
  }
  return <div className={classes}>{body}</div>;
};

/* ------------------------------------------------------------------- Tabs */

export interface TabItem<T extends string = string> {
  value: T;
  label: React.ReactNode;
  count?: number;
  icon?: React.ReactNode;
}

export interface TabsProps<T extends string = string> {
  items: TabItem<T>[];
  value: T;
  onChange: (value: T) => void;
  className?: string;
  /** "underline" sits on a page header; "pill" is a compact segmented control */
  variant?: "underline" | "pill";
}

export function Tabs<T extends string = string>({
  items,
  value,
  onChange,
  className,
  variant = "underline",
}: TabsProps<T>) {
  if (variant === "pill") {
    return (
      <div role="tablist" className={cn("inline-flex rounded-xl bg-gray-100 p-1", className)}>
        {items.map((item) => {
          const active = item.value === value;
          return (
            <button
              key={item.value}
              role="tab"
              type="button"
              aria-selected={active}
              onClick={() => onChange(item.value)}
              className={cn(
                "inline-flex h-8 items-center gap-1.5 rounded-lg px-3 text-meta font-medium transition-all [&_svg]:size-4",
                active ? "bg-white text-gray-900 shadow-sm" : "text-gray-600 hover:text-gray-900"
              )}
            >
              {item.icon}
              {item.label}
              {item.count !== undefined && (
                <span className={cn("tabular text-xs", active ? "text-gray-500" : "text-gray-400")}>
                  {item.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div
      role="tablist"
      className={cn("scrollbar-none -mb-px flex gap-6 overflow-x-auto border-b border-gray-200", className)}
    >
      {items.map((item) => {
        const active = item.value === value;
        return (
          <button
            key={item.value}
            role="tab"
            type="button"
            aria-selected={active}
            onClick={() => onChange(item.value)}
            className={cn(
              "relative inline-flex h-11 shrink-0 items-center gap-2 text-sm font-medium transition-colors [&_svg]:size-4",
              active ? "text-gray-900" : "text-gray-500 hover:text-gray-800"
            )}
          >
            {item.icon}
            {item.label}
            {item.count !== undefined && (
              <span
                className={cn(
                  "tabular rounded-md bg-gray-100 px-1.5 text-xs font-medium",
                  active ? "text-gray-900" : "text-gray-500"
                )}
              >
                {item.count}
              </span>
            )}
            <span
              className={cn(
                "absolute inset-x-0 -bottom-px h-0.5 rounded-full transition-colors",
                active ? "bg-gray-900" : "bg-transparent"
              )}
            />
          </button>
        );
      })}
    </div>
  );
}

/* --------------------------------------------------------------- Progress */

export interface ProgressProps extends React.HTMLAttributes<HTMLDivElement> {
  /** 0–max */
  value: number;
  max?: number;
  /** Accessible name, e.g. "Profile completion" */
  label?: string;
  tone?: "brand" | "success" | "warning" | "danger" | "neutral";
  size?: "sm" | "md";
}

const progressTones = {
  brand: "bg-brand-500",
  success: "bg-emerald-500",
  warning: "bg-amber-500",
  danger: "bg-red-500",
  neutral: "bg-gray-900",
};

/** Thin determinate progress bar (role="progressbar") */
export const Progress: React.FC<ProgressProps> = ({
  value,
  max = 100,
  label,
  tone = "brand",
  size = "md",
  className,
  ...props
}) => {
  const pct = Math.min(100, Math.max(0, max > 0 ? (value / max) * 100 : 0));
  return (
    <div
      role="progressbar"
      aria-valuenow={Math.round(value)}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-label={label}
      className={cn(
        "w-full overflow-hidden rounded-full bg-gray-100",
        size === "sm" ? "h-1.5" : "h-2",
        className
      )}
      {...props}
    >
      <div
        className={cn("h-full rounded-full transition-[width] duration-300 ease-out", progressTones[tone])}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
};
